/**
 * 手動編輯 ＋ 換風格（UI v2 步 6：由 `ChatPage.tsx` 抽出，零行為改動）
 *
 *   · 手動編輯：訊息內逐句高亮 ＋ 輸入 →〔交畀 AI 改〕／〔直接套用〕（`decisions/026`）
 *   · 換風格：`openStyle` ／ `runRemake`（`decisions/026`／`034`）
 */
import { useState } from 'react';
import type { TFunction } from 'i18next';
import type { StyleOption } from '@coeldery/style-picker';
import { buildStylePrompt } from '@coeldery/style-picker';
import { applyLineEdits, splitLyricLines } from '@coeldery/lyrics-editor';
import type { SheetKind, VocalGender } from './types';

export interface UseSongEditDeps {
  t: TFunction;
  sessionId: string;
  deviceId: string;
  locale: string;
  vocalGender: VocalGender;
  lyricsOf: (songId: string) => string;
  styleOptions: StyleOption[];
  setSheet: (s: SheetKind) => void;
  setBusyAction: (v: boolean) => void;
  setActionError: (v: string | null) => void;
  setRenderingId: (v: string | null) => void;
  reload: () => void;
}

export function useSongEdit(deps: UseSongEditDeps) {
  const { t, sessionId, deviceId, locale, vocalGender, lyricsOf, styleOptions, setSheet, setBusyAction, setActionError, setRenderingId, reload } = deps;

  /** 手動編輯（訊息內逐句高亮 ＋ 輸入） */
  const [editSongId, setEditSongId] = useState<string | null>(null);
  const [editPicked, setEditPicked] = useState<number[]>([]);
  const [editDrafts, setEditDrafts] = useState<Record<number, string>>({});
  /** 換風格 */
  const [styleSongId, setStyleSongId] = useState<string | null>(null);
  const [styleSelected, setStyleSelected] = useState<string[]>([]);
  const [styleFree, setStyleFree] = useState('');

  const startEdit = (songId: string) => {
    setEditSongId(songId);
    setEditPicked([]);
    setEditDrafts({});
  };
  const toggleEditLine = (i: number) =>
    setEditPicked((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]));
  const setDraft = (i: number, v: string) => setEditDrafts((prev) => ({ ...prev, [i]: v }));
  const exitEdit = () => {
    setEditSongId(null);
    setEditPicked([]);
    setEditDrafts({});
  };

  /** 砌「邊幾句要改」嘅指令（交畀 AI） */
  const editInstruction = (songId: string): string => {
    const lines = splitLyricLines(lyricsOf(songId));
    return editPicked
      .map((i) => {
        const want = (editDrafts[i] ?? '').trim();
        return `第 ${i + 1} 句（原句：${lines[i] ?? ''}）${want ? `改成：${want}` : '請寫得更好、更貼近我原本嘅意思'}`;
      })
      .join('\n');
  };

  /** 〔交畀 AI 改〕（唔花音樂錢） */
  const applyWithAi = async (songId: string) => {
    setBusyAction(true);
    setActionError(null);
    try {
      const res = await fetch(`/api/song/${songId}/revise`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, deviceId, instruction: editInstruction(songId) }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) setActionError(`${t('song_error_generic')}${data.error ? `（${data.error}）` : ''}`);
      exitEdit();
      reload();
    } catch (err) {
      setActionError(`${t('song_error_generic')}（${String(err).slice(0, 80)}）`);
    } finally {
      setBusyAction(false);
    }
  };

  /** 〔直接套用〕（你打嘅字逐句替換；唔花錢） */
  const applyDirect = async (songId: string) => {
    const base = lyricsOf(songId);
    const next = applyLineEdits(base, editDrafts);
    if (next === base) {
      setActionError(t('edit_line_placeholder'));
      return;
    }
    setBusyAction(true);
    setActionError(null);
    try {
      const res = await fetch(`/api/song/${songId}/lyrics`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, deviceId, lyrics: next }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) setActionError(`${t('song_error_generic')}${data.error ? `（${data.error}）` : ''}`);
      exitEdit();
      reload();
    } catch (err) {
      setActionError(`${t('song_error_generic')}（${String(err).slice(0, 80)}）`);
    } finally {
      setBusyAction(false);
    }
  };

  /* ── 換風格 ── */
  const openStyle = (songId: string) => {
    setStyleSongId(songId);
    setStyleSelected([]);
    setStyleFree('');
    setSheet('style');
  };
  const runRemake = async () => {
    if (!styleSongId) return;
    const instruction = buildStylePrompt(styleSelected, styleOptions, styleFree);
    setSheet('none');
    setBusyAction(true);
    setActionError(null);
    try {
      const res = await fetch(`/api/song/${styleSongId}/remake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, deviceId, instruction, locale, vocalGender }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string; song?: { id?: string } };
      if (!res.ok) setActionError(`${t('song_error_generic')}${data.error ? `（${data.error}）` : ''}`);
      else setRenderingId(data.song?.id ?? styleSongId);
      reload();
    } catch (err) {
      setActionError(`${t('song_error_generic')}（${String(err).slice(0, 80)}）`);
    } finally {
      setBusyAction(false);
    }
  };

  return {
    editSongId, editPicked, editDrafts,
    startEdit, toggleEditLine, setDraft, exitEdit,
    applyWithAi, applyDirect,
    styleSongId, styleSelected, setStyleSelected, styleFree, setStyleFree,
    openStyle, runRemake,
  };
}
